import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { ArrowLeft, Save, CheckCircle2, Loader2, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { onboardingAPI } from '../../lib/onboardingAPI';
import { clearanceAPI } from '../../lib/clearanceAPI';

const FIELDS = [['firm_name', 'Firm name'], ['accountant_name', 'Accountant name'], ['email', 'Email'], ['phone', 'Phone'], ['address', 'Address'],
  ['services', 'Services previously provided'], ['financial_years', 'Financial years involved (e.g. 2022-23 to 2024-25)']];
const ANSWERS = [['yes', 'Yes'], ['no', 'No'], ['unknown', 'Unknown – I will confirm']];
const norm = (v) => (v === true ? 'yes' : v === false ? 'no' : v || '');

const Answer = ({ value, onChange, testid }) => (
  <div className="flex flex-wrap gap-4">{ANSWERS.map(([k, l]) => (
    <label key={k} className="flex items-center gap-2 cursor-pointer text-sm">
      <input type="radio" checked={value === k} onChange={() => onChange(k)} data-testid={`${testid}-${k}`} />{l}</label>))}</div>
);

const AccountantFields = ({ f, set, testid }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{FIELDS.map(([k, l]) => (
      <div key={k} className={`space-y-1 ${k === 'address' || k === 'services' ? 'md:col-span-2' : ''}`}><Label>{l}</Label>
        <Input value={f[k] || ''} onChange={(e) => set({ ...f, [k]: e.target.value })} data-testid={`${testid}-${k}`} /></div>))}</div>
    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.clearance_required !== false} onChange={(e) => set({ ...f, clearance_required: e.target.checked })} data-testid={`${testid}-clearance`} />Professional clearance should be requested</label>
    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.records_transfer_requested} onChange={(e) => set({ ...f, records_transfer_requested: e.target.checked })} data-testid={`${testid}-records`} />Please request my records from this accountant</label>
  </div>
);

export default function PreviousAccountantForm({ clientId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ has_previous_accountant: '', clearance_required: true, same_for_group: true, entities: [], notes: '' });
  const [others, setOthers] = useState([]);

  useEffect(() => {
    if (!clientId) return;
    Promise.all([onboardingAPI.getSection(clientId, 'previous_accountant').catch(() => ({ data: {} })), clearanceAPI.myEntities().catch(() => [])])
      .then(([res, ents]) => {
        const rd = res.data?.response_data || {};
        setF((s) => ({ ...s, ...rd, has_previous_accountant: norm(rd.has_previous_accountant) }));
        setOthers(ents.filter((e) => !e.primary));
      })
      .finally(() => setLoading(false));
  }, [clientId]);

  const ent = (id) => (f.entities || []).find((e) => e.entity_id === id) || { entity_id: id, clearance_required: true };
  const setEnt = (id, v) => setF((s) => ({ ...s, entities: [...(s.entities || []).filter((e) => e.entity_id !== id), { ...v, entity_id: id }] }));

  const save = async (status) => {
    const yes = f.has_previous_accountant === 'yes';
    if (status === 'completed' && !f.has_previous_accountant) { toast.error('Please answer whether you have a previous accountant'); return; }
    if (status === 'completed' && yes && !(f.email && (f.firm_name || f.accountant_name))) { toast.error("Please enter your previous accountant's firm or name and email"); return; }
    setSaving(true);
    try {
      const entities = others.map((o) => ({ ...ent(o.id), entity_name: o.name }));
      await onboardingAPI.saveSection(clientId, 'previous_accountant', { status, response_data: { ...f, entities, authority_given: false } });
      toast.success(status === 'completed' ? 'Section marked complete' : 'Saved — you can return any time');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;
  const yes = f.has_previous_accountant === 'yes';

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/onboarding')} className="text-muted-foreground -ml-2"><ArrowLeft className="w-4 h-4 mr-2" /> Back to checklist</Button>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Previous Accountant</CardTitle>
          <CardDescription>Does this client or entity currently have, or previously have, another accountant or tax agent?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <Answer value={f.has_previous_accountant} onChange={(v) => setF({ ...f, has_previous_accountant: v })} testid="prev-acc" />
          {yes && <>
            <div className="flex items-start gap-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <Briefcase className="w-4 h-4 mt-0.5 flex-shrink-0" />
              We will ask you to sign an authority before we contact your previous accountant for professional clearance and your records. This is standard practice when changing accountants.
            </div>
            <AccountantFields f={f} set={setF} testid="prev-acc" />
          </>}
          {others.length > 0 && <div className="space-y-3 border-t pt-4" data-testid="prev-acc-entities">
            <p className="font-medium text-sm">Other entities in your group</p>
            {yes && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.same_for_group} onChange={(e) => setF({ ...f, same_for_group: e.target.checked })} data-testid="prev-acc-same-group" />Use the same accountant for all entities in this group</label>}
            {!(yes && f.same_for_group) && others.map((o) => {
              const e = ent(o.id);
              return (
                <div key={o.id} className="rounded-md border p-3 space-y-2" data-testid={`prev-acc-entity-${o.id}`}>
                  <p className="text-sm font-medium">{o.name}</p>
                  <Answer value={norm(e.has_previous_accountant)} onChange={(v) => setEnt(o.id, { ...e, has_previous_accountant: v })} testid={`prev-acc-entity-${o.id}`} />
                  {norm(e.has_previous_accountant) === 'yes' && <AccountantFields f={e} set={(v) => setEnt(o.id, v)} testid={`prev-acc-entity-${o.id}`} />}
                </div>);
            })}
          </div>}
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea rows={3} value={f.notes || ''} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Anything we should know — e.g. outstanding fees, documents they hold…" />
          </div>
          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={() => save('in_progress')} disabled={saving} data-testid="prev-acc-save"><Save className="w-4 h-4 mr-2" /> Save &amp; return later</Button>
            <Button onClick={() => save('completed')} disabled={saving} data-testid="prev-acc-complete"><CheckCircle2 className="w-4 h-4 mr-2" /> Mark as complete</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
